import { createBrowserRouter, redirect } from 'react-router-dom';
import React from 'react';
import ErrorPage from '../public/ErrorPage';
import Chat from './chat/Chat';
import ChatHistoryPage from './chat/ChatHistory';
import ApplicationLayout from '../../components/layout/ApplicationLayout';
import DefaultGrid from '../../layouts/defaultGrid/DefaultGrid';
import { RouteGuard } from '../../components/routeGuard/RouteGuard';
import { withSuspense } from '../../utils/withSuspense';
import DefaultPage from './root/DefaultPage';
// Admin Pages
const TenantsIndex = React.lazy(() => import('./admin/tenants/TenantsIndex'));
const CreateTenant = React.lazy(() => import('./admin/tenants/CreateTenant'));
const TenantDetail = React.lazy(() => import('./admin/tenants/TenantDetail'));
const AdminTenantEdit = React.lazy(() => import('./admin/tenants/AdminTenantEdit'));

// Knowledge Base Subpages
const KnowledgeBaseLayout = React.lazy(() => import('./kb/KnowledgeBaseLayout'));
const KnowledgeBaseIndex = React.lazy(() => import('./kb/KnowledgeBaseIndex'));
const CreateKnowledgeBase = React.lazy(() => import('./kb/CreateKnowledgeBase'));
const KnowledgeBaseDetail = React.lazy(() => import('./kb/KnowledgeBaseDetail'));
const KnowledgeBaseEdit = React.lazy(() => import('./kb/KnowledgeBaseEdit'));
const KnowledgeBaseSources = React.lazy(() => import('./kb/KnowledgeBaseSources'));
const CreateSource = React.lazy(() => import('./kb/CreateSource'));

// Tenant Management
const TenantLayout = React.lazy(() => import('./tenant/TenantLayout'));
const ManageMembers = React.lazy(() => import('./tenant/ManageMembers'));
const InviteUser = React.lazy(() => import('./tenant/InviteUser'));
const TenantSettings = React.lazy(() => import('./tenant/TenantSettings'));

// Assistant Subpages
const AssistantIndex = React.lazy(() => import('./assistants/AssistantIndex'));
const CreateAssistant = React.lazy(() => import('./assistants/CreateAssistant'));
const AssistantDetail = React.lazy(() => import('./assistants/AssistantDetail'));
const AssistantEdit = React.lazy(() => import('./assistants/AssistantEdit'));

// MCP Servers (tenant-owned external tool providers)
const McpServersIndex = React.lazy(() => import('./mcp-servers/McpServersIndex'));
const CreateMcpServer = React.lazy(() => import('./mcp-servers/CreateMcpServer'));
const McpServerDetail = React.lazy(() => import('./mcp-servers/McpServerDetail'));
const EditMcpServer = React.lazy(() => import('./mcp-servers/EditMcpServer'));

// Users
const UserPage = React.lazy(() => import('./users/UserProfile'));

// Invites
const AcceptInvite = React.lazy(() => import('./invites/AcceptInvite'));
const PendingInvitations = React.lazy(() => import('./invites/PendingInvitations'));

export const Router = createBrowserRouter([
  {
    element: <ApplicationLayout />,
    errorElement: <ErrorPage />,
    handle: {
      crumb: () => 'Home'
    },
    children: [
      {
        path: '/',
        element: <DefaultPage />
      },
      {
        path: 'chat/:id?',
        element: <Chat />
      },
      {
        path: 'chat-history',
        element: <ChatHistoryPage />,
        handle: { crumb: () => 'Chat History' }
      },
      {
        path: 'admin',
        element: (
          <RouteGuard requireSiteAdmin>
            <DefaultGrid />
          </RouteGuard>
        ),
        children: [
          {
            path: 'organizations',
            children: [
              {
                index: true,
                element: withSuspense(TenantsIndex)
              },
              {
                path: 'create',
                element: withSuspense(CreateTenant),
                handle: { crumb: () => 'Create Tenant' }
              },
              {
                path: ':tenantId',
                element: withSuspense(TenantDetail),
                handle: { crumb: () => 'Tenant Detail' }
              },
              {
                path: ':tenantId/edit',
                element: withSuspense(AdminTenantEdit),
                handle: { crumb: () => 'Edit Tenant' }
              }
            ]
          }
        ]
      },
      {
        path: 'user',
        element: <DefaultGrid />,
        children: [
          {
            path: 'profile',
            element: withSuspense(UserPage)
          },
          {
            path: 'pending-invitations',
            element: withSuspense(PendingInvitations)
          }
        ]
      },
      {
        path: 'organization',
        element: <RouteGuard requireTenantAccess>{withSuspense(TenantLayout)}</RouteGuard>,
        children: [
          {
            path: 'members',
            element: <RouteGuard requireTenantOwnerOrAdmin>{withSuspense(ManageMembers)}</RouteGuard>
          },
          {
            path: 'settings',
            element: <RouteGuard requireTenantOwnerOrAdmin>{withSuspense(TenantSettings)}</RouteGuard>,
            handle: { crumb: () => 'Settings' }
          },
          {
            path: 'knowledge-bases',
            element: withSuspense(KnowledgeBaseIndex)
          },
          {
            path: 'assistants',
            element: withSuspense(AssistantIndex)
          },
          {
            path: 'invite',
            element: <RouteGuard requireTenantOwnerOrAdmin>{withSuspense(InviteUser)}</RouteGuard>,
            handle: { crumb: () => 'Invite User' }
          },
          {
            path: 'knowledge-bases',
            element: withSuspense(KnowledgeBaseIndex),
            handle: { crumb: () => 'Knowledge Bases' }
          },
          {
            path: 'knowledge-bases/create',
            element: withSuspense(CreateKnowledgeBase),
            handle: { crumb: () => 'Create Knowledge Base' }
          },
          {
            path: 'knowledge-bases/:knowledgeBaseId',
            element: withSuspense(KnowledgeBaseLayout),
            handle: { crumb: () => 'Knowledge Bases', path: () => '/organization/knowledge-bases' },
            children: [
              {
                index: true,
                element: withSuspense(KnowledgeBaseDetail),
                handle: { crumb: () => 'Details' }
              },
              {
                path: 'edit',
                element: withSuspense(KnowledgeBaseEdit),
                handle: { crumb: () => 'Edit' }
              },
              {
                path: 'sources',
                element: withSuspense(KnowledgeBaseSources),
                handle: { crumb: () => 'Sources' }
              },
              {
                path: 'sources/create',
                element: withSuspense(CreateSource),
                handle: { crumb: () => 'Create Source' }
              }
            ]
          },
          {
            path: 'assistants/create',
            element: <RouteGuard requireTenantOwnerOrAdmin>{withSuspense(CreateAssistant)}</RouteGuard>,
            handle: { crumb: () => 'Create Assistant' }
          },
          {
            path: 'assistants/:assistantId',
            element: <RouteGuard requireTenantOwnerOrAdmin>{withSuspense(AssistantDetail)}</RouteGuard>,
            handle: { crumb: () => 'Assistants', path: () => '/organization/assistants' }
          },
          {
            path: 'assistants/:assistantId/edit',
            element: <RouteGuard requireTenantOwnerOrAdmin>{withSuspense(AssistantEdit)}</RouteGuard>,
            handle: { crumb: () => 'Edit Assistant' }
          },
          {
            path: 'tools/servers',
            element: <RouteGuard requireTenantOwnerOrAdmin>{withSuspense(McpServersIndex)}</RouteGuard>,
            handle: { crumb: () => 'MCP Servers' }
          },
          {
            path: 'tools/servers/create',
            element: <RouteGuard requireTenantOwnerOrAdmin>{withSuspense(CreateMcpServer)}</RouteGuard>,
            handle: { crumb: () => 'Register MCP Server' }
          },
          {
            path: 'tools/servers/:serverId',
            element: <RouteGuard requireTenantOwnerOrAdmin>{withSuspense(McpServerDetail)}</RouteGuard>,
            handle: { crumb: () => 'MCP Server' }
          },
          {
            path: 'tools/servers/:serverId/edit',
            element: <RouteGuard requireTenantOwnerOrAdmin>{withSuspense(EditMcpServer)}</RouteGuard>,
            handle: { crumb: () => 'Edit MCP Server' }
          }
        ]
      },
      {
        path: 'invite/accept/:inviteId',
        element: withSuspense(AcceptInvite)
      },
      {
        path: '*',
        loader: () => redirect('/')
      }
    ]
  }
]);
